import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Heart, Shield, Users, FileCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function NonProfit() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Heart, title: 'Tax-Exempt Status', description: 'Qualify for federal and state tax exemptions under 501(c)(3).' },
    { icon: Shield, title: 'Liability Protection', description: 'Protect board members and officers from personal liability.' },
    { icon: Users, title: 'Grant Eligibility', description: 'Access foundation grants and government funding opportunities.' },
    { icon: FileCheck, title: 'Credibility', description: 'Build trust with donors and the community through official status.' },
  ];

  const faqs = [
    { question: 'What is a Non-Profit organization?', answer: 'A non-profit organization is a business entity organized for charitable, educational, religious, scientific, or literary purposes that qualifies for tax-exempt status.' },
    { question: 'How long does it take to form a non-profit?', answer: 'Formation typically takes 3-6 months, including state incorporation and IRS 501(c)(3) tax-exempt status approval.' },
    { question: 'What are the ongoing compliance requirements?', answer: 'Non-profits must file annual Form 990 with the IRS, maintain proper governance, hold board meetings, and comply with state charitable solicitation requirements.' },
    { question: 'Can non-profit founders receive compensation?', answer: 'Yes, founders and staff can receive reasonable compensation for services rendered. However, excess compensation can jeopardize tax-exempt status.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1559027615-cd4628902d4a?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Non-Profit Formation</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Form your non-profit organization and make a difference with tax-exempt status.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Form a Non-Profit?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Create lasting impact with tax-exempt non-profit status.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about non-profit formation.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Start Your Non-Profit?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Begin your mission with professional non-profit formation services.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="non_profit" />
      <Footer />
    </div>
  );
}